import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { formatBytes, TICKET_ACCEPT, TICKET_CHANNEL_WEB, filesToTicketPayload } from './ticketHelpers';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 5;

function NewTicketPage() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState('');
    const [body, setBody] = useState('');
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get('/tickets/meta')
            .then((response) => {
                const list = Array.isArray(response?.data?.categories) ? response.data.categories : [];
                setCategories(list);
                if (list.length > 0) {
                    setCategory(list[0].code);
                }
            })
            .catch(() => setCategories([]))
            .finally(() => setLoadingMeta(false));
    }, []);

    const onFilesChange = (event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        const next = [...files];
        const localErrors = {};
        for (const file of selected) {
            if (next.length >= MAX_FILES) {
                localErrors.files = `No máximo ${MAX_FILES} arquivos.`;
                break;
            }
            if (file.size > MAX_FILE_BYTES) {
                localErrors.files = `${file.name} excede 5 MB.`;
                continue;
            }
            next.push(file);
        }
        setFiles(next);
        setErrors((prev) => ({ ...prev, files: localErrors.files || undefined }));
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setErrors({});
        try {
            const payload = {
                category,
                channel: TICKET_CHANNEL_WEB,
                body: body.trim(),
                files: await filesToTicketPayload(files),
            };
            const response = await api.post('/tickets', payload);
            const id = response?.data?.data?.id;
            await Swal.fire({ icon: 'success', title: 'Ticket aberto', timer: 1400, showConfirmButton: false });
            navigate(id ? `/admin/tickets/${id}` : '/admin/tickets');
        } catch (error) {
            const data = error?.response?.data;
            if (data?.errors) {
                setErrors(data.errors);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Não foi possível abrir o ticket',
                    text: data?.message || data?.error || 'Tente novamente.',
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingMeta) {
        return <Loading />;
    }

    return (
        <Box component="form" onSubmit={handleSubmit} maxWidth={720}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Admin
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/tickets">
                    Tickets
                </Link>
                <Typography color="text.primary">Novo</Typography>
            </Breadcrumbs>

            <Typography variant="h5" fontWeight={700} mb={1}>
                Abrir ticket
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Descreva o problema ou solicitação em texto livre. Você pode anexar imagens, vídeos, logs, STL e outros arquivos (até 5 MB cada).
            </Typography>

            <Stack spacing={2.5}>
                <FormControl fullWidth error={Boolean(errors.category)}>
                    <InputLabel id="ticket-category-label">Categoria</InputLabel>
                    <Select
                        labelId="ticket-category-label"
                        label="Categoria"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        {categories.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.category ? <FormHelperText>{errors.category}</FormHelperText> : null}
                </FormControl>

                <TextField
                    label="Detalhes"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    multiline
                    minRows={6}
                    fullWidth
                    error={Boolean(errors.body)}
                    helperText={errors.body || 'Sem formatação — apenas texto.'}
                />

                <Box>
                    <Button variant="outlined" component="label" disabled={submitting}>
                        Anexar arquivos
                        <input hidden type="file" multiple accept={TICKET_ACCEPT} onChange={onFilesChange} />
                    </Button>
                    {errors.files ? (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {errors.files}
                        </Alert>
                    ) : (
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                            Até {MAX_FILES} arquivos · máx. 5 MB cada
                        </Typography>
                    )}
                    <Stack spacing={0.5} mt={1}>
                        {files.map((file, index) => (
                            <Stack key={`${file.name}-${index}`} direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2">
                                    {file.name} ({formatBytes(file.size)})
                                </Typography>
                                <Button size="small" onClick={() => removeFile(index)}>
                                    Remover
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'Enviando…' : 'Abrir ticket'}
                    </Button>
                    <Button variant="outlined" disabled={submitting} onClick={() => navigate('/admin/tickets')}>
                        Cancelar
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

export default NewTicketPage;
